/**
 * diagnostic-score.ts
 * Pure, side-effect-free scoring engine for narrative diagnostics.
 * No React, no Supabase — inputs in, score out.
 */
import type {
  NarrativeAnalysis,
  CharacterArc,
  EmotionalMapItem,
  NarrativeDiagnostic,
  Conflict,
  Theme,
} from '@/types/scriptAnalysis';

// ── Tuneable constants ──────────────────────────────────────────────────────
const PROBLEM_HIGH   = -2;
const PROBLEM_MED    = -1;
const PROBLEM_LOW    = -0.5;
const STRENGTH_HIGH  = +1;
const STRENGTH_MED   = +0.5;
const STRENGTH_LOW   = +0.25;

// Global area weights (must sum to 1.0)
const WEIGHTS: Record<Area, number> = {
  estructura:  0.25,
  personajes:  0.25,
  emociones:   0.15,
  ritmo:       0.15,
  temas:       0.10,
  dialogos:    0.10,
};

// Key beats we look for in turning_points[].type (loose match, lower-cased).
// ALINEADOS con el enum real del schema (narrative-schemas.ts): inciting_incident,
// first_turn, midpoint, second_turn, climax, resolution. Antes se buscaba
// `all_is_lost` (que el schema NO permite → todo guion lo veía "ausente" y perdía
// ese punto) y `second_turn` estaba dentro de `climax` (falso positivo de clímax).
const KEY_BEAT_PATTERNS: Record<string, string[]> = {
  inciting_incident: ['inciting_incident', 'inciting', 'detonante', 'incidente'],
  first_turn:        ['first_turn', 'plot_point_1', 'primer_giro', 'first_plot_point', 'pp1'],
  midpoint:          ['midpoint', 'punto_medio'],
  second_turn:       ['second_turn', 'segundo_giro', 'plot_point_2', 'pp2', 'all_is_lost', 'todo_perdido', 'crisis'],
  climax:            ['climax', 'clímax'],
};

// Keyword fallback for diagnostics without area set
const AREA_KEYWORDS: Record<Area, string[]> = {
  estructura:  ['estructura', 'acto', 'paradigma', 'giro', 'turning', 'beat'],
  personajes:  ['personaje', 'protagonista', 'arco', 'character', 'arc'],
  emociones:   ['emoción', 'emocional', 'tensión', 'emocion', 'tension', 'intensidad', 'emotional'],
  ritmo:       ['ritmo', 'pacing', 'meseta', 'plateau', 'velocidad', 'pace'],
  dialogos:    ['diálogo', 'dialogo', 'voz', 'dialogue', 'dialog', 'voice'],
  temas:       ['tema', 'temátic', 'tematic', 'motivo', 'motif', 'theme'],
};

// ── Public types ────────────────────────────────────────────────────────────
export type Area = 'estructura' | 'personajes' | 'emociones' | 'ritmo' | 'dialogos' | 'temas';

export const AREA_LABELS: Record<Area, string> = {
  estructura: 'Estructura',
  personajes: 'Personajes',
  emociones:  'Emociones',
  ritmo:      'Ritmo',
  dialogos:   'Diálogos',
  temas:      'Temas',
};

export interface AreaScore {
  area:    Area;
  label:   string;
  score:   number;   // 1–10, 1 decimal
  base:    number;   // structural base before diagnostic adjustments
  adjust:  number;   // sum of severity adjustments
  drivers: string[]; // 2–4 human-readable strings
  excluded?: boolean; // sin señal real → se muestra pero NO cuenta en el global
}

export interface DiagnosticScoreResult {
  areas:  AreaScore[];
  global: number;  // weighted average, 1 decimal
}

export interface DiagnosticScoreInputs {
  narrative:    NarrativeAnalysis     | null | undefined;
  arcs:         CharacterArc[]        | null | undefined;
  emotionalMap: EmotionalMapItem[]    | null | undefined;
  themes:       Theme[]               | null | undefined;
  conflicts:    Conflict[]            | null | undefined;
  diagnostics:  NarrativeDiagnostic[] | null | undefined;
  cast:         { dialogue_lines?: number | null }[] | null | undefined; // scene_cast → señal de diálogo
  sceneCount:   number | null | undefined;                               // nº de escenas (densidad)
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

function round1(v: number): number {
  return Math.round(v * 10) / 10;
}

function matchesBeat(typeStr: string, patterns: string[]): boolean {
  const lower = typeStr.toLowerCase();
  return patterns.some(p => lower.includes(p));
}

function severityAdjust(d: NarrativeDiagnostic): number {
  if (d.type === 'problem') {
    if (d.severity === 'high')   return PROBLEM_HIGH;
    if (d.severity === 'medium') return PROBLEM_MED;
    return PROBLEM_LOW;
  } else {
    if (d.severity === 'high')   return STRENGTH_HIGH;
    if (d.severity === 'medium') return STRENGTH_MED;
    return STRENGTH_LOW;
  }
}

const VALID_AREAS = new Set<string>(['estructura', 'personajes', 'emociones', 'ritmo', 'dialogos', 'temas']);

/** ¿Es una nota del PIPELINE (no un diagnóstico del guion)? Por `area="sistema"`
 * (análisis nuevos) o por la descripción del validador anti-alucinación (análisis
 * viejos que aún no llevan el área). No puntúa ni sale en la lista del guionista. */
export function isSystemDiagnostic(d: { area?: string | null; description?: string | null }): boolean {
  if (d.area === 'sistema') return true;
  return (d.description ?? '').toLowerCase().startsWith('validador anti-alucin');
}

/** Assign a diagnostic to an area: use d.area si es un área válida, si no keyword.
 * Un `area` NO válida (p. ej. "sistema", que marca las notas del pipeline/validador
 * anti-alucinación) devuelve null → NO puntúa (no penaliza la nota del guion por
 * fallos del pipeline). */
function assignArea(d: NarrativeDiagnostic): Area | null {
  if (isSystemDiagnostic(d)) return null; // nota del pipeline → no puntúa
  if (d.area) return VALID_AREAS.has(d.area) ? (d.area as Area) : null;
  const desc = (d.description ?? '').toLowerCase();
  for (const [area, keywords] of Object.entries(AREA_KEYWORDS) as [Area, string[]][]) {
    if (keywords.some(k => desc.includes(k))) return area;
  }
  return null;
}

// ── Per-area base score computers ────────────────────────────────────────────

function baseEstructura(
  narrative: NarrativeAnalysis | null | undefined,
): { base: number; drivers: string[] } {
  if (!narrative) return { base: 4, drivers: ['Sin datos de análisis narrativo'] };

  const tps = narrative.turning_points ?? [];

  // Cine NO CONVENCIONAL / de autor / documental: NO se evalúa contra la estructura
  // de Field (3 actos). Antes salía base 4 + "Ausentes: inciting incident…" como si
  // faltara algo → castigaba justo lo que el ICAA más financia. Base neutra; los
  // beats reconocibles suman como BONUS, su ausencia no penaliza ni se lista.
  if (narrative.paradigm === 'non_conventional') {
    let base = 6;
    const drivers = ['Estructura no convencional — no se evalúa contra el paradigma de 3 actos'];
    const found = Object.values(KEY_BEAT_PATTERNS).filter(p => tps.some(tp => matchesBeat(tp.type ?? '', p))).length;
    if (found > 0) {
      base += Math.min(found * 0.5, 2);
      drivers.push(`${found} beat${found > 1 ? 's' : ''} reconocible${found > 1 ? 's' : ''} (bonus)`);
    }
    return { base: round1(clamp(base, 1, 10)), drivers };
  }

  let base = 4;
  const drivers: string[] = [];

  // Key beats: +1 per distinct beat present (max +5)
  const found: string[] = [];
  const missing: string[] = [];
  for (const [beatName, patterns] of Object.entries(KEY_BEAT_PATTERNS)) {
    if (tps.some(tp => matchesBeat(tp.type ?? '', patterns))) {
      base += 1;
      found.push(beatName);
    } else {
      missing.push(beatName);
    }
  }

  if (found.length > 0) {
    drivers.push(`${found.length} beat${found.length > 1 ? 's' : ''} clave presente${found.length > 1 ? 's' : ''}`);
  }
  if (missing.length > 0) {
    const label = missing.map(b => b.replace(/_/g, ' ')).join(', ');
    drivers.push(`Ausentes: ${label}`);
  }

  // Act balance
  const acts = narrative.acts;
  if (acts) {
    const a1 = (acts.act1_scenes ?? []).length;
    const a2 = (acts.act2_scenes ?? []).length;
    const a3 = (acts.act3_scenes ?? []).length;
    const total = a1 + a2 + a3;
    if (total > 0) {
      const act2pct = a2 / total;
      if (act2pct >= 0.40 && act2pct <= 0.62) {
        base += 1;
        drivers.push(`Acto 2 equilibrado (${Math.round(act2pct * 100)}% del total)`);
      } else if (act2pct < 0.30 || act2pct > 0.72) {
        base -= 1;
        drivers.push(`Acto 2 desequilibrado (${Math.round(act2pct * 100)}%)`);
      }
    }
  }

  return { base, drivers };
}

function basePersonajes(
  arcs:      CharacterArc[] | null | undefined,
  conflicts: Conflict[]     | null | undefined,
): { base: number; drivers: string[] } {
  let base = 4;
  const drivers: string[] = [];

  const arcList = arcs ?? [];

  if (arcList.length === 0) {
    drivers.push('Sin arcos de personaje detectados');
    return { base, drivers };
  }

  const completeArc = (a: CharacterArc): boolean =>
    (a.objective ?? '').trim().length > 20 &&
    (a.obstacle  ?? '').trim().length > 20 &&
    (a.transformation ?? '').trim().length > 20;

  const prota = arcList.reduce(
    (best, a) => (a.presence_pct ?? 0) > (best.presence_pct ?? 0) ? a : best,
    arcList[0],
  );

  if (completeArc(prota)) {
    base += 3;
    drivers.push(`Arco del protagonista completo (${prota.name})`);
  } else {
    base += 1;
    drivers.push(`Protagonista presente pero arco incompleto (${prota.name})`);
  }

  const completeCount = arcList.filter(completeArc).length;
  if (completeCount >= 2) {
    base += 1;
    drivers.push(`${completeCount} arcos de personaje completos`);
  }

  const conflictList = conflicts ?? [];
  if (conflictList.some(c => (c.resolution ?? '').trim().length > 0)) {
    base += 1;
    drivers.push('Al menos un conflicto tiene resolución definida');
  }

  return { base, drivers };
}

function baseEmociones(
  emotionalMap: EmotionalMapItem[] | null | undefined,
): { base: number; drivers: string[] } {
  const map = emotionalMap ?? [];
  if (map.length === 0) {
    return { base: 6, drivers: ['Sin mapa emocional (neutro por defecto)'] };
  }

  // intensity field is typed number; clamp defensively to [0,10]
  const intensities = map.map(e => clamp(Number(e.intensity ?? 5), 0, 10));
  // RANGO INTERCUARTÍLICO (IQR) en vez de rango max−min: el max−min lo inflaba UN
  // outlier (1 escena a 10 sobre un guion plano daba rango 5). El IQR mide la
  // variación del 50% central → robusto a picos aislados (una escena intensa no
  // "salva" una curva plana).
  const sorted = [...intensities].sort((a, b) => a - b);
  const pct = (p: number) => {
    const idx = (sorted.length - 1) * p;
    const lo = Math.floor(idx), hi = Math.ceil(idx);
    return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
  };
  const iqr = pct(0.75) - pct(0.25);
  // IQR 0 → 3 (curva plana); IQR ~4 → ~9 (buena variación sostenida).
  const base = clamp(3 + iqr * 1.5, 1, 10);
  const drivers: string[] = [`Variación emocional (IQR): ${round1(iqr)}/10`];
  if (iqr < 1)  drivers.push('Curva emocional muy plana');
  else if (iqr >= 3) drivers.push('Buena variación emocional a lo largo del guion');

  return { base: round1(base), drivers };
}

function baseRitmo(
  emotionalMap: EmotionalMapItem[] | null | undefined,
): { base: number; drivers: string[] } {
  const map = emotionalMap ?? [];
  if (map.length === 0) {
    return { base: 6, drivers: ['Sin mapa emocional (neutro por defecto)'] };
  }

  const intensities = map.map(e => clamp(Number(e.intensity ?? 5), 0, 10));

  // Count plateaus: runs of ≥3 consecutive scenes where intensity changes < 0.5
  const PLATEAU_THRESHOLD = 0.5;
  const PLATEAU_MIN_RUN   = 3;
  let plateauCount = 0;
  let run = 1;
  for (let i = 1; i < intensities.length; i++) {
    if (Math.abs(intensities[i] - intensities[i - 1]) < PLATEAU_THRESHOLD) {
      run++;
      if (run === PLATEAU_MIN_RUN) plateauCount++;
    } else {
      run = 1;
    }
  }

  let base = clamp(8 - plateauCount * 0.5, 1, 10);

  // Reward transitions (change ≥ 1.5 between consecutive scenes)
  const transitions = intensities.filter(
    (v, i) => i > 0 && Math.abs(v - intensities[i - 1]) >= 1.5,
  ).length;
  if (transitions >= Math.floor(intensities.length * 0.2)) {
    base = clamp(base + 0.5, 1, 10);
  }

  const drivers: string[] = [];
  if (plateauCount === 0) {
    drivers.push('Sin mesetas de ritmo detectadas');
  } else {
    drivers.push(`${plateauCount} meseta${plateauCount > 1 ? 's' : ''} de ritmo detectada${plateauCount > 1 ? 's' : ''}`);
  }
  drivers.push(`${transitions} transiciones de intensidad`);

  return { base: round1(base), drivers };
}

function baseTemas(
  themes: Theme[] | null | undefined,
): { base: number; drivers: string[] } {
  let base = 4;
  const drivers: string[] = [];

  const themeList  = themes ?? [];
  const mainThemes = themeList.filter(t => t.type === 'main');

  if (mainThemes.length >= 1) {
    base += 2;
    drivers.push(
      `${mainThemes.length} tema${mainThemes.length > 1 ? 's' : ''} principal${mainThemes.length > 1 ? 'es' : ''} identificado${mainThemes.length > 1 ? 's' : ''}`,
    );
  } else {
    drivers.push('Sin temas principales identificados');
  }

  const embodiedMain = mainThemes.filter(
    t => (t.embodied_by ?? []).length > 0 && (t.scene_numbers ?? []).length > 0,
  );
  if (embodiedMain.length >= 1) {
    base += 2;
    drivers.push('Tema principal encarnado en personajes y escenas');
  } else if (mainThemes.length >= 1) {
    drivers.push('Tema principal sin embodiment (personajes o escenas vacíos)');
  }

  return { base: clamp(base, 1, 10), drivers };
}

// Señal REAL de diálogo desde el parser (scene_cast.dialogue_lines), en vez del 7
// fijo. Mide la ARQUITECTURA del diálogo (nº de voces, reparto de la carga, densidad
// por escena), no el ingenio/subtexto — eso lo aportan los diagnósticos del LLM del
// área. Un guion sin diálogo (visual) no se penaliza: se marca sin señal.
function baseDialogos(
  cast:       { dialogue_lines?: number | null }[] | null | undefined,
  sceneCount: number | null | undefined,
): { base: number; drivers: string[]; hasData: boolean } {
  const speaking = (cast ?? [])
    .map(c => Number(c.dialogue_lines ?? 0))
    .filter(n => n > 0)
    .sort((a, b) => b - a);
  if (speaking.length === 0) {
    return { base: 5, drivers: ['Sin diálogo detectado (¿guion visual?)'], hasData: false };
  }

  const totalLines = speaking.reduce((s, n) => s + n, 0);
  const voices = speaking.length;
  const topShare = speaking[0] / totalLines;
  const density = (sceneCount ?? 0) > 0 ? totalLines / (sceneCount as number) : 0;

  let base = 6;
  const drivers: string[] = [];
  if (voices >= 4) { base += 1; drivers.push(`${voices} voces con diálogo (reparto coral)`); }
  else if (voices >= 2) { drivers.push(`${voices} voces con diálogo`); }
  else { drivers.push('Una sola voz con diálogo (monólogo / voz en off)'); }

  // Reparto de la carga de diálogo (solo con 3+ voces: con 1-2 no aplica).
  if (voices >= 3) {
    if (topShare <= 0.55) { base += 1; drivers.push('Diálogo bien repartido entre el reparto'); }
    else if (topShare >= 0.80) { base -= 1; drivers.push(`Diálogo muy concentrado: una voz acapara el ${Math.round(topShare * 100)}%`); }
  }
  drivers.push(`${round1(density)} líneas de diálogo por escena`);

  return { base: round1(clamp(base, 1, 10)), drivers, hasData: true };
}

// ── Main export ──────────────────────────────────────────────────────────────
export function computeDiagnosticScore(inputs: DiagnosticScoreInputs): DiagnosticScoreResult {
  const { narrative, arcs, emotionalMap, themes, conflicts, diagnostics, cast, sceneCount } = inputs;
  const diagList = diagnostics ?? [];

  // Assign each diagnostic to an area
  const byArea: Record<Area, NarrativeDiagnostic[]> = {
    estructura: [], personajes: [], emociones: [], ritmo: [], dialogos: [], temas: [],
  };
  for (const d of diagList) {
    const area = assignArea(d);
    if (area) byArea[area].push(d);
  }

  // Compute structural bases
  const estResult = baseEstructura(narrative);
  const perResult = basePersonajes(arcs, conflicts);
  const emoResult = baseEmociones(emotionalMap);
  const rytResult = baseRitmo(emotionalMap);
  const temResult = baseTemas(themes);
  const diaResult = baseDialogos(cast, sceneCount);

  const areaData: Record<Area, { base: number; structDrivers: string[] }> = {
    estructura: { base: estResult.base, structDrivers: estResult.drivers },
    personajes: { base: perResult.base, structDrivers: perResult.drivers },
    emociones:  { base: emoResult.base, structDrivers: emoResult.drivers },
    ritmo:      { base: rytResult.base, structDrivers: rytResult.drivers },
    dialogos:   { base: diaResult.base, structDrivers: diaResult.drivers },
    temas:      { base: temResult.base, structDrivers: temResult.drivers },
  };

  // Áreas SIN señal real no deben contar en el global (antes puntuaban 6/7 "neutro"
  // y pesaban ~30% sin datos): emociones/ritmo sin mapa emocional, y diálogos (aún
  // sin señal del parser → base fija). Se muestran, pero se EXCLUYEN del global y se
  // renormalizan los pesos sobre las áreas con datos.
  const hasMap = (emotionalMap?.length ?? 0) > 0;
  const hasData: Record<Area, boolean> = {
    estructura: !!narrative,
    personajes: (arcs?.length ?? 0) > 0,
    emociones:  hasMap,
    ritmo:      hasMap,
    temas:      (themes?.length ?? 0) > 0,
    dialogos:   diaResult.hasData, // ahora hay señal real del parser (scene_cast)
  };

  const areas: AreaScore[] = (Object.keys(areaData) as Area[]).map(area => {
    const adiags  = byArea[area];
    // Cap del ajuste a ±3: sin él, 5 problemas graves = -10 → suelo 1 (una avalancha
    // de hallazgos del LLM hundía el área). El ajuste modula la base, no la sustituye.
    const adjust  = round1(clamp(adiags.reduce((sum, d) => sum + severityAdjust(d), 0), -3, 3));
    const score   = round1(clamp(areaData[area].base + adjust, 1, 10));

    // Drivers: structural explanations + diagnostic summary
    const drivers: string[] = [...areaData[area].structDrivers];
    const hProblems = adiags.filter(d => d.type === 'problem' && d.severity === 'high').length;
    const mProblems = adiags.filter(d => d.type === 'problem' && d.severity === 'medium').length;
    const nStrengths = adiags.filter(d => d.type === 'strength').length;
    if (hProblems  > 0) drivers.push(`${hProblems} problema${hProblems  > 1 ? 's' : ''} grave${hProblems  > 1 ? 's' : ''}`);
    if (mProblems  > 0) drivers.push(`${mProblems} problema${mProblems  > 1 ? 's' : ''} medio${mProblems  > 1 ? 's' : ''}`);
    if (nStrengths > 0) drivers.push(`${nStrengths} fortaleza${nStrengths > 1 ? 's' : ''} en esta área`);

    return {
      area,
      label:   AREA_LABELS[area],
      score,
      base:    round1(areaData[area].base),
      adjust,
      drivers: drivers.slice(0, 4),
      excluded: !hasData[area],
    };
  });

  // Global = media ponderada SOLO sobre áreas con datos, renormalizando sus pesos.
  const scored = areas.filter(a => !a.excluded);
  const totalW = scored.reduce((sum, a) => sum + WEIGHTS[a.area], 0) || 1;
  const global = round1(scored.reduce((sum, a) => sum + a.score * WEIGHTS[a.area], 0) / totalW);

  return { areas, global };
}
